import { useState } from 'react';
import {
  Radar,
  Gauge,
  Waypoints,
  SignalHigh,
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Check,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CyclePhaseBar } from '@/components/CyclePhaseBar';
import { cn } from '@/lib/utils';

interface Step {
  icon: typeof Radar;
  title: string;
  body: React.ReactNode;
}

const STEPS: Step[] = [
  {
    icon: Radar,
    title: 'Welcome to BCH Radar',
    body: (
      <>
        <p>
          BCH Radar is a 24/7 macro-liquidity dashboard for Bitcoin Cash. It
          watches global money conditions and BCH&apos;s setup, then distills
          everything into two simple scores so you can grasp the picture in
          about ten seconds.
        </p>
        <p className="text-muted-foreground">
          Here&apos;s how to read it. This takes under a minute.
        </p>
      </>
    ),
  },
  {
    icon: Gauge,
    title: 'The two scores',
    body: (
      <>
        <p>
          <strong className="text-primary">Global Liquidity</strong> asks: are
          worldwide money conditions getting easier or tighter? Higher is more
          supportive for risk assets.
        </p>
        <p>
          <strong className="text-chart-2">BCH Setup</strong> asks: is
          BCH&apos;s environment improving versus Bitcoin?
        </p>
        <div className="rounded-lg border border-border/60 bg-secondary/40 p-3">
          <p className="text-sm">
            Both run on a <strong>0–100</strong> scale.{' '}
            <span className="font-mono font-semibold">50</span> is neutral —
            above it leans supportive, below it leans restrictive. The dashed
            line on each chart marks that 50 midpoint.
          </p>
        </div>
      </>
    ),
  },
  {
    icon: Waypoints,
    title: 'The liquidity cycle',
    body: (
      <>
        <p>
          Liquidity moves through repeating phases. The Radar places today
          somewhere on this path and shows where it&apos;s heading next.
        </p>
        <div className="rounded-lg border border-border/60 bg-secondary/40 p-3">
          <CyclePhaseBar phase="easing" />
          <p className="mt-3 text-xs text-muted-foreground">
            Tightening → Bottoming → Easing → Expansion → Mania. Early phases
            are restrictive; later phases are abundant and risk-friendly.
          </p>
        </div>
      </>
    ),
  },
  {
    icon: SignalHigh,
    title: 'Honest signals',
    body: (
      <>
        <p>
          Instead of shouting &ldquo;buy&rdquo; or &ldquo;sell,&rdquo; the Radar
          describes <em>conditions</em> with confidence-aware labels:
        </p>
        <ul className="space-y-1.5 text-sm">
          <li className="flex items-center gap-2">
            <span aria-hidden>🟢</span> Confirmed / developing — supportive and
            trending up
          </li>
          <li className="flex items-center gap-2">
            <span aria-hidden>🟡</span> Neutral — mixed or low-confidence
          </li>
          <li className="flex items-center gap-2">
            <span aria-hidden>🟠</span> Warning — weakening
          </li>
          <li className="flex items-center gap-2">
            <span aria-hidden>🔴</span> Confirmed negative — restrictive and
            trending down
          </li>
        </ul>
        <p className="text-muted-foreground">
          A signal only turns strong when both the level <em>and</em> the trend
          agree — and when there&apos;s enough fresh data to trust it.
        </p>
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: 'Transparent data',
    body: (
      <>
        <p>
          Every indicator shows its source and freshness. Live readings are
          marked <span className="font-medium text-primary">fresh</span>; older
          ones are flagged <span className="font-medium">stale</span> and never
          silently faked.
        </p>
        <p>
          Tap any factor — on the Radar or the Signals page — for the full
          breakdown of what it measures and why it matters. The{' '}
          <strong>Sources</strong> page lists every feed.
        </p>
        <p className="text-muted-foreground">
          Not financial advice. Signals describe conditions, not predictions.
        </p>
      </>
    ),
  },
];

export function OnboardingDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const Icon = current.icon;

  const close = () => {
    onOpenChange(false);
    // Reset for next time the dialog is opened.
    setTimeout(() => setStep(0), 200);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) close();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          <DialogTitle className="text-xl">{current.title}</DialogTitle>
          <DialogDescription className="sr-only">
            BCH Radar walkthrough, step {step + 1} of {STEPS.length}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
          {current.body}
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pt-1">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              aria-label={`Go to step ${i + 1}`}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === step ? 'w-5 bg-primary' : 'w-1.5 bg-border hover:bg-muted-foreground/50',
              )}
            />
          ))}
        </div>

        <div className="mt-1 flex items-center justify-between gap-2">
          {isFirst ? (
            <Button variant="ghost" size="sm" onClick={close}>
              Skip
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          )}

          {isLast ? (
            <Button className="gap-1.5" onClick={close}>
              <Check className="h-4 w-4" /> Start exploring
            </Button>
          ) : (
            <Button className="gap-1.5" onClick={() => setStep((s) => s + 1)}>
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
